import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from "react";
import Router from "next/router";
import { useReportWebVitals } from "next/web-vitals";
const REPORTED_VITALS = ["INP", "LCP", "TTFB", "CLS"];
const SESSIONS_ROUTE = "/[org]/sessions/[[...sessionPath]]";

/**
 * The new-session page (`/{org}/sessions/new`) shares the sessions catch-all
 * route pattern with session threads; report it under its own label so the
 * two chart separately in Grafana. The session segment comes from `asPath`
 * (`/acme/sessions/new` → `new`) rather than `Router.query`, which is still
 * empty when TTFB and LCP fire on a statically optimized first load.
 */
function reportedRoute(): string {
  if (Router.pathname !== SESSIONS_ROUTE) return Router.pathname;
  const path = Router.asPath.split(/[?#]/)[0];
  const sessionSegment = path.split("/").filter(Boolean)[2];
  return sessionSegment === "new" ? "/[org]/sessions/new" : SESSIONS_ROUTE;
}

/**
 * Reports the page's web vitals (INP, LCP, TTFB, CLS) to `/frontend/metrics`
 * so they chart over time in Grafana, labeled by the pages-router route
 * pattern (e.g. `/[org]/logs`).
 *
 * web-vitals emits INP and CLS when the page is first hidden (tab switch,
 * close) and again on later hides if the value grew; only the first emission
 * per metric id is reported, since a Prometheus histogram cannot retract the
 * earlier sample. Sent via `fetch` with `keepalive` and a form-encoded body:
 * the CORS-safelisted content type needs no preflight, which browsers often
 * cancel during unload, exactly when INP and CLS fire.
 */
export function useWebVitalsReporting() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const reportedIds = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = metric => {
      if (!REPORTED_VITALS.includes(metric.name)) {
        return;
      }
      if (reportedIds.current.has(metric.id)) {
        return;
      }
      reportedIds.current.add(metric.id);
      fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/frontend/metrics`, {
        method: "POST",
        keepalive: true,
        credentials: "include",
        body: new URLSearchParams({
          web_vital: metric.name,
          web_vital_value: String(metric.value),
          web_vital_route: reportedRoute()
        })
      }).catch(_temp);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useReportWebVitals(t1);
}
function _temp() {}